import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {AbstractControlOptions, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import moment from 'moment';
import {debounceTime, distinctUntilChanged, filter, Subject, take, takeUntil, tap} from 'rxjs';
import {HistoryService} from 'src/app/modules/core/services/history.service';
import {HistoryStore, SearchStore, VehicleStore} from 'src/app/modules/core/store';
import {
    SearchConfig,
    SearchPropertyKey,
    SearchResultItem,
    SearchStrategy,
    Vehicle
} from 'src/app/modules/shared/models';
import {
    checkIfEndDateAfterStartDate,
    checkIfEndDateStartDateDiffIsBiggerThanADay,
    checkIfEndTimeAndDateIsBeforeNow,
    dateValidator,
    futureDateValidator,
    getHistoryMinDate,
    timeValidator,
    tooOldDateValidator
} from './customValidators';
import {Router} from '@angular/router';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatButtonModule} from '@angular/material/button';
import {TranslateModule} from '@ngx-translate/core';
import {FeedbackTextComponent} from '../../modules/shared/components/feedback-text/feedback-text.component';
import {SpinnerComponent} from '../../modules/shared/components/spinner/spinner.component';
import {MatIconModule} from '@angular/material/icon';

import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDatepickerModule} from '@angular/material/datepicker';


@Component({
    selector: 'app-history',
    templateUrl: './history.component.html',
    styleUrls: ['./history.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        SpinnerComponent,
        FeedbackTextComponent,
        TranslateModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatDatepickerModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})

export class HistoryComponent implements OnInit, OnDestroy {
    private _formBuilder = inject(FormBuilder);
    private _historyService = inject(HistoryService);
    private _historyStore = inject(HistoryStore);
    private _searchStore = inject(SearchStore);
    private _vehicleStore = inject(VehicleStore);
    private _router = inject(Router);
    private _route = inject(ActivatedRoute);

    public reactiveFormObject;
    public searchHitItem: SearchResultItem;
    public isSearchingVehicle = false;
    public maxDate = moment().format('YYYY-MM-DD');
    public minDate = moment(getHistoryMinDate()).format('YYYY-MM-DD');

    public isFetchingHistory = false;
    public isHistoryDataIncomplete = false;

    private _ngUnsubscribe$ = new Subject<void>();
    private _oldQuery;
    private _timer;
    private _previousStartDate;
    private _previousNumberOfVehicles: number;

    private readonly _defaultDatePickerDate: string = moment().format('YYYY-MM-DD');


    constructor() {
        this.reactiveFormObject = this._initForm();
    }

    ngOnInit(): void {
        // Deep-linking: check for vehicle/vehicleId, startDateTime, endDateTime, view=history in query params
        this._route.queryParams.pipe(takeUntil(this._ngUnsubscribe$)).subscribe(async params => {
            const vehicleId = params['vehicle'] ?? params['vehicleId'];
            const startDateTime = params['startDateTime'];
            const endDateTime = params['endDateTime'];
            const view = params['view'];
            if (vehicleId && startDateTime && endDateTime && view === 'history') {
                // Pre-fill form and trigger search
                this.reactiveFormObject.patchValue({
                    searchQuery: vehicleId,
                    startDate: moment(startDateTime).format('YYYY-MM-DD'),
                    endDate: moment(endDateTime).format('YYYY-MM-DD'),
                    startTime: moment(startDateTime).format('HH:mm'),
                    endTime: moment(endDateTime).format('HH:mm')
                });
                // Wait for vehicle search to complete
                this._searchVehicle(vehicleId, true);
                // Wait for searchHitItem to be set by subscription
                const waitForSearch = () => new Promise<void>(resolve => {
                    this._searchStore.getPropertyState('searchResults')
                        .pipe(
                            filter(items => !!items && items.length > 0),
                            take(1)
                        )
                        .subscribe(items => {
                            this.searchHitItem = items[0];
                            resolve();
                        });
                });
                await waitForSearch();
                await this.searchVehiclePositionHistory();
            }
        });

        // Listens to user typing and performs search
        this.reactiveFormObject.get('searchQuery').valueChanges.pipe(
            tap(() => this.isSearchingVehicle = true),
            debounceTime(500),
            distinctUntilChanged(),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((query: string) => {
            this._searchVehicle(query);
        });

        // Listens to potential hits in the search
        this._searchStore.getPropertyState('searchResults')
            .pipe(
                takeUntil(this._ngUnsubscribe$)
            )
            .subscribe(items => {
                // search hit
                if (items && items.length > 0) {
                    clearTimeout(this._timer);
                    this.searchHitItem = items[0];
                    this.isSearchingVehicle = false;
                } else { // delay feedback and error messages if no search hit
                    clearTimeout(this._timer);
                    this._timer = setTimeout(() => {
                        this.isSearchingVehicle = false;
                        this.searchHitItem = undefined;
                    }, 1000);
                }
            });

        // listen to changes in number of vehicles to detect if the user added or removed garages and the we have to redo the search
        this._vehicleStore.getVehiclesState().pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((vehicles: Vehicle[]) => {
            if (vehicles?.length !== this._previousNumberOfVehicles) {
                this._searchVehicle(this._oldQuery, true);
                this._previousNumberOfVehicles = vehicles?.length ?? 0;
            }
        });

        // Listen to startTime changes to automatically set endTime if previously unset
        this.reactiveFormObject.get('startTime').valueChanges.pipe(
            debounceTime(1000),
            distinctUntilChanged(),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((startTime: string) => {
            if (
                !this.reactiveFormObject.value['endTime']
                || this.reactiveFormObject.value['endTime'] === ''
                || this.reactiveFormObject.get('endTime').invalid
            ) {
                const endTime = moment(startTime, 'HH:mm').add(30, 'minutes').format('HH:mm');
                this.reactiveFormObject.patchValue({
                    endTime
                });
            }
        });

        // Listen to startDate changes to automatically set endDate to same, if previously unchanged
        this.reactiveFormObject.get('startDate').valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((newStartDate: string) => {
            if (this.reactiveFormObject.value['endDate'] === this._previousStartDate) {
                this.reactiveFormObject.patchValue({
                    endDate: newStartDate
                });
            }
            this._previousStartDate = newStartDate;
        });
    }

    ngOnDestroy(): void {
        this._historyStore.resetData();

        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();

        clearTimeout(this._timer);
    }

    public get searchQueryString() {
        return this.reactiveFormObject.controls['searchQuery'].value;
    }

    public preventDefault(event) {
        event.preventDefault();
    }

    public formEnterClicked(event) {
        this.preventDefault(event);
        this.searchVehiclePositionHistory();
    }

    public async searchVehiclePositionHistory() {
        if (this.reactiveFormObject.valid && this.searchHitItem) {
            this.isFetchingHistory = true;
            this.isHistoryDataIncomplete = false;
            this._historyStore.resetData();

            const vehicleHistory = await this._historyService.getVehicleHistory(
                this.searchHitItem.id,
                this.reactiveFormObject.value['startDate'],
                this.reactiveFormObject.value['endDate'],
                this.reactiveFormObject.value['startTime'],
                this.reactiveFormObject.value['endTime'],
            );

            this.isFetchingHistory = false;

            if (!vehicleHistory || vehicleHistory.passedPositions?.length <= 1) {
                this.isHistoryDataIncomplete = true;
                const right0Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right0']?.segments.map(s => s.path) ?? null;
                const right1Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right1']?.segments.map(s => s.path) ?? null;
                const right2Segments = this._router.parseUrl(this._router.url)
                    .root.children['outlet-right2']?.segments.map(s => s.path) ?? null;
                this._router.navigate(['', {
                    outlets: { outlet0: null, outlet1: null, outlet2: null, 'outlet-right0': right0Segments, 'outlet-right1': right1Segments, 'outlet-right2': right2Segments }
                }]);
                return;
            }

            this._historyStore.updateHistoryData(vehicleHistory);
            this._historyStore.updateHighlightedTime(vehicleHistory.passedPositions[0]);

            // Keep history results focused and ensure stale side blades are closed.
            this._router.navigate(['', { outlets: { outlet0: ['history'], outlet1: null, outlet2: null } }]);
        }
    }

    public reset(event) {
        event.preventDefault();
        this._resetForm();
        this._historyStore.resetData();
        this.searchHitItem = undefined;
        this.isHistoryDataIncomplete = false;
    }

    public showPropValidationMessage(prop: string, messageName: string): boolean {
        if (this.isSearchingVehicle) {
            return false;
        }

        // show warning if the field has been touched, is invalid and there is a warning for the messageName
        if ((this.reactiveFormObject.controls[prop].dirty
            || this.reactiveFormObject.controls[prop].touched)
            && this.reactiveFormObject.controls[prop].invalid) {
            return (this.reactiveFormObject.get(prop)?.errors?.[messageName]);
        }

        return false;
    }

    public showFormValidationMessage(messageName: string): boolean {
        if (this.isSearchingVehicle) {
            return false;
        }

        if (this.reactiveFormObject.hasError(messageName)) {
            return true;
        }

        return false;
    }

    private _searchVehicle(query: string, forceSearch: boolean = false) {
        this.isSearchingVehicle = true;
        this.searchHitItem = undefined;
        this.isHistoryDataIncomplete = false;

        if (!forceSearch && query === '' || query == undefined) {
            this._oldQuery = undefined;
            this.isSearchingVehicle = false;
            return;
        }

        if (forceSearch || this._oldQuery !== query) {
            this._oldQuery = query;
            const searchConfig: SearchConfig = {
                minQueryLength: 4,
                searchPropertyKey: SearchPropertyKey.inventoryNumberWithoutPrefix,
                searchStrategy: SearchStrategy.ExactSearch
            }
            this._searchStore.searchAsync(query, [searchConfig]);
            return;
        }

        this.isSearchingVehicle = false;
    }

    private _resetForm() {
        this.reactiveFormObject.markAsPristine();
        this.reactiveFormObject.markAsUntouched();
        this.reactiveFormObject.updateValueAndValidity();
        this.reactiveFormObject.reset();
        this.reactiveFormObject.patchValue({
            searchQuery: '',
            startDate: this._defaultDatePickerDate,
            endDate: this._defaultDatePickerDate,
            startTime: '',
            endTime: ''
        });
        this._previousStartDate = this._defaultDatePickerDate;
    }

    private _initForm(): FormGroup {
        this._previousStartDate = this._defaultDatePickerDate;
        return this._formBuilder.group({
            searchQuery: [
                '',
                Validators.required
            ],
            startDate: [
                this._defaultDatePickerDate,
                [Validators.required, dateValidator, futureDateValidator, tooOldDateValidator]
            ],
            endDate: [
                this._defaultDatePickerDate,
                [Validators.required, dateValidator, tooOldDateValidator]
            ],
            startTime: [
                '',
                [Validators.required, timeValidator]
            ],
            endTime: [
                '',
                [Validators.required, timeValidator]
            ]
        },
            {
                validators: [checkIfEndDateAfterStartDate, checkIfEndTimeAndDateIsBeforeNow, checkIfEndDateStartDateDiffIsBiggerThanADay]
            } as AbstractControlOptions);
    }

}
